import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectAdminService } from '@app/services/admin/project-admin.service';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';

type UiStatus = 'ok' | 'expired' | 'invalid';
type ActionType = 'accept' | 'cancel' | 'cancelByContact';

@Component({
  selector: 'app-meeting-response',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './meeting-response.component.html',
  styleUrl: './meeting-response.component.scss',
})
export class MeetingResponseComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private projectAdminService = inject(ProjectAdminService);

  readonly loading = signal<boolean>(true);
  readonly status = signal<UiStatus | null>(null);

  ngOnInit(): void {
    const meetingId = this.route.snapshot.queryParamMap.get('meetingId') ?? '';
    const dateId = this.route.snapshot.queryParamMap.get('dateId') ?? '';
    const choice = (
      this.route.snapshot.queryParamMap.get('choice') ?? ''
    ).toLowerCase();
    const by = (
      this.route.snapshot.queryParamMap.get('by') ?? ''
    ).toLowerCase();

    const action = this.normalizeChoice(choice, by);

    if (!action || !meetingId || (action === 'accept' && !dateId)) {
      this.loading.set(false);
      this.status.set('invalid');
      return;
    }

    const request =
      action === 'accept'
        ? this.projectAdminService.acceptMeetingByCustomer(meetingId, dateId)
        : action === 'cancelByContact'
          ? this.projectAdminService.cancelMeetingByContact(meetingId)
          : this.projectAdminService.cancelMeetingByCustomer(meetingId);

    request.pipe(finalize(() => this.loading.set(false))).subscribe({
      next: () => {
        this.status.set('ok');
        this.router.navigate([], {
          relativeTo: this.route,
          replaceUrl: true,
          queryParams: {
            meetingId: null,
            dateId: null,
            choice: null,
            by: null,
          },
          queryParamsHandling: 'merge',
        });
      },
      error: (err) => {
        this.status.set(this.resolveErrorStatus(err));
      },
    });
  }

  private resolveErrorStatus(err: unknown): UiStatus {
    const error = (err as { error?: { type?: string; title?: string; message?: string } })
      ?.error;
    const joined = `${error?.type ?? ''} ${error?.title ?? ''} ${error?.message ?? ''}`.toLowerCase();
    return joined.includes('expired') ? 'expired' : 'invalid';
  }

  private normalizeChoice(choice: string, by: string): ActionType | null {
    if (choice === 'accept' || choice === 'accepted' || choice === '1')
      return 'accept';
    if (choice === 'cancel' || choice === 'reject' || choice === '0') {
      return by === 'contact' ? 'cancelByContact' : 'cancel';
    }
    return null;
  }
}
